import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { HeaderBar } from "@/components/grc/HeaderBar";
import { AuditWarningBanner } from "@/components/grc/AuditWarningBanner";
import { ZoneA } from "@/components/grc/ZoneA";
import { ZoneB } from "@/components/grc/ZoneB";
import { ZoneC } from "@/components/grc/ZoneC";
import { ZoneD } from "@/components/grc/ZoneD";
import { PillarDetailModal } from "@/components/grc/PillarDetailModal";
import { MOCK_GRC_DASHBOARD } from "@/components/grc/mock";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useRole } from "@/context/RoleContext";
import { fmtDateTime } from "@/components/grc/shared";
import { Eye } from "lucide-react";
import { SupportModeBar } from "@/components/platform/SupportMode";

const LoadingSkeleton = () => (
  <div className="space-y-6" data-testid="dashboard-loading-skeleton">
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-64 rounded-xl" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <Skeleton className="h-96 rounded-xl lg:col-span-4" />
      <Skeleton className="h-96 rounded-xl lg:col-span-5" />
      <Skeleton className="h-96 rounded-xl lg:col-span-3" />
    </div>
  </div>
);

export default function GRCDashboard() {
  const { id } = useParams();
  const role = useRole();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: dash } = await api.get(`/grc/${id}/dashboard`);
      setData(dash);
      setUsingMock(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchDashboard();
  }, [id, fetchDashboard]);

  const pillarForModal = useMemo(
    () => (selectedPillar && data?.pillars ? data.pillars[selectedPillar] : null),
    [selectedPillar, data]
  );

  if (!id) return <Navigate to="/dashboard" replace />;

  const loadDemo = () => {
    setData({ ...MOCK_GRC_DASHBOARD, assessment_id: id });
    setUsingMock(true);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="grc-dashboard-page">
      <HeaderBar
        orgName={data?.org_profile?.org_name}
        assessmentId={id}
        deadline={data?.compliance_deadline}
        daysRemaining={data?.days_until_deadline}
        schemaVersion={data?.schema_version}
      />
      <SupportModeBar />

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 space-y-6">
        {role === "viewer" && (
          <div
            className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2.5 flex items-center gap-2 text-xs text-slate-600"
            data-testid="read-only-banner"
          >
            <Eye size={14} />
            Read-only access — you can view all data but cannot make changes. Contact your administrator for edit rights.
          </div>
        )}
        {loading && <LoadingSkeleton />}

        {!loading && error && (
          <div
            className="rounded-xl border border-slate-200 bg-white p-10 text-center space-y-4"
            data-testid="dashboard-error-state"
          >
            <div className="text-sm text-slate-600">Failed to load dashboard.</div>
            <div className="text-xs text-slate-400 font-mono">{String(error)}</div>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={fetchDashboard} data-testid="dashboard-retry-button" className="bg-[#008080] hover:bg-[#1a51db]">
                Retry
              </Button>
              <Button variant="outline" onClick={loadDemo} data-testid="dashboard-load-demo-button">
                Load demo data
              </Button>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {usingMock && (
              <div
                className="text-[11px] font-mono px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-900 inline-block"
                data-testid="mock-data-badge"
              >
                DEMO DATA — not from a live import
              </div>
            )}
            {data.audit_deadline_warning === true && <AuditWarningBanner />}
            <ZoneA data={data} />
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
              <div className="xl:col-span-8 min-w-0">
                <ZoneB data={data} onPillarClick={setSelectedPillar} />
              </div>
              <div className="xl:col-span-4 min-w-0">
                <ZoneC data={data} assessmentId={id} />
              </div>
            </div>
            <ZoneD assessmentId={id} />
            <footer
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500"
              data-testid="dashboard-footer-strip"
            >
              <span>
                Scoring based on CX0 DPDP Gap Assessment {data.schema_version || "V2.2"} · 15 Active Pillars ·
                Waterline {data.waterline_pct}% · {data.pillars_below_waterline_count} pillars below waterline
              </span>
              <span>
                Data as of: {data.last_imported_at ? fmtDateTime(data.last_imported_at) : "—"}
              </span>
            </footer>
          </>
        )}
      </main>

      <PillarDetailModal
        assessmentId={id}
        pillar={pillarForModal}
        sdf={data?.sdf}
        waterline={data?.waterline_pct}
        open={!!selectedPillar}
        onClose={() => setSelectedPillar(null)}
      />
    </div>
  );
}
